import React from "react";
import { useTranslation } from "react-i18next";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import TimeSelect from "components/timeSelect/TimeSelect";
import BatchOptionGroup from "./BatchOptionGroup";

export type BatchPostMode = "now" | "draft" | "schedule";

type Props = {
  value: BatchPostMode;
  onChange: (mode: BatchPostMode) => void;
  scheduledDate: Date;
  onScheduledDateChange: (date: Date) => void;
  scheduledTime: string;
  onScheduledTimeChange: (time: string | undefined) => void;
};

export default function BatchPostModeSection(props: Props) {
  const { t } = useTranslation();
  const {
    value,
    onChange,
    scheduledDate,
    onScheduledDateChange,
    scheduledTime,
    onScheduledTimeChange,
  } = props;

  return (
    <BatchOptionGroup
      role="radiogroup"
      aria-label={t("When to post")}
      options={[
        {
          key: "now",
          title: t("Post now"),
          description: t("Run this batch immediately after you confirm."),
          selected: value === "now",
          onSelect: () => onChange("now"),
        },
        {
          key: "draft",
          title: t("Save as draft"),
          description: t("Save the batch to edit or post later from the Batches tab."),
          selected: value === "draft",
          onSelect: () => onChange("draft"),
        },
        {
          key: "schedule",
          title: t("Post date"),
          description: t("Schedule the batch to run automatically at a future date and time."),
          selected: value === "schedule",
          onSelect: () => onChange("schedule"),
          content: (
            <div className="batch-option-group__schedule-fields">
              <DatePickerInput
                label={t("Date")}
                months={1}
                position="left"
                size="small"
                startingDate={scheduledDate}
                setStartingDate={date => {
                  const next = typeof date === "function" ? date(scheduledDate) : date;
                  if (next) {
                    onScheduledDateChange(next);
                  }
                }}
                disablePastDays
              />
              <TimeSelect
                label={t("Time")}
                value={scheduledTime}
                onChange={onScheduledTimeChange}
                step={15}
                usePortal
                placeholder={t("Select a time")}
              />
            </div>
          ),
        },
      ]}
    />
  );
}
